import { useState } from 'react';
import { UserBalance } from '../types/pool';

/**
 * PaymentCard (Payment Tracking v2)
 *
 * Shows what the user owes, big pay buttons with the amount prefilled, and
 * their pay code with a copy button. Venmo links prefill the note with the
 * code automatically; for PayPal / Cash App the user pastes it.
 */

const METHOD_COLORS: Record<string, string> = {
  PAYPAL: '#0070ba',
  CASHAPP: '#00d632',
  VENMO: '#3d95ce',
};

const fmtMoney = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2));

interface PaymentCardProps {
  balance: UserBalance;
}

export function PaymentCard({ balance }: PaymentCardProps) {
  const [copied, setCopied] = useState(false);

  if (balance.totalOwed <= 0) return null;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(balance.payCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the code is visible to copy manually
    }
  };

  const needsManualNote = balance.paymentMethods.some((m) => !m.notePrefilled);

  return (
    <div className="border-2 border-amber-400 bg-amber-50 rounded-xl p-5">
      <h3 className="text-lg font-bold text-amber-900 m-0">
        Total owed: ${fmtMoney(balance.totalOwed)}
      </h3>

      {/* Pay code */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <div className="bg-white border-2 border-amber-300 rounded-lg px-4 py-2">
          <span className="text-xs text-amber-700 font-medium block">Your pay code</span>
          <span className="text-xl font-mono font-bold text-amber-900 tracking-wider">
            {balance.payCode}
          </span>
        </div>
        <button
          type="button"
          onClick={copyCode}
          className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {copied ? '✓ Copied!' : 'Copy code'}
        </button>
      </div>
      <p className="text-sm text-amber-800 mt-2 mb-4">
        Put this code in the payment note so your payment is matched to your
        entries instantly.
      </p>

      {/* Pay buttons */}
      <div className="flex gap-3 flex-wrap">
        {balance.paymentMethods.map((pm) => (
          <a
            key={pm.method}
            href={pm.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-85 transition-opacity"
            style={{ backgroundColor: METHOD_COLORS[pm.method] || '#6b7280' }}
          >
            Pay ${fmtMoney(balance.totalOwed)} with {pm.label}
            <span className="text-xs opacity-90">({pm.handle})</span>
          </a>
        ))}
      </div>
      {balance.paymentMethods.length === 0 && (
        <p className="text-sm text-amber-800">
          Payment links aren't configured yet — contact the pool host to arrange
          payment.
        </p>
      )}
      {needsManualNote && (
        <p className="text-xs text-amber-700 mt-2">
          Venmo fills in your code automatically. Paying with PayPal or Cash App?
          Paste <span className="font-mono font-bold">{balance.payCode}</span> into
          the note.
        </p>
      )}

      <p className="text-sm text-amber-800 mt-3 mb-0">
        Your entries are active either way — the unpaid badge clears once your
        payment is confirmed.
      </p>
    </div>
  );
}
